import type React from 'react';

import * as styles from './styles';
import {
  FieldArray,
  type FieldArrayRenderProps,
  useFormikContext,
} from 'formik';

import {
  type Checklist,
  type ChecklistField,
  ChecklistFieldType,
  type TemplateChecklistField,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  CaptionDefault,
  type DropdownOption,
} from '@cobliteam/base-components';

import InfoIcon from '@material-ui/icons/Info';

import ChecklistItem, { checklistDefaultContent } from '../../ChecklistItem';
import ChecklistItemDropdownMenu from '../../ChecklistItem/ChecklistItemDropdownMenu';
import { FooterButtons } from '../FooterButtons/FooterButtons';

export interface DrawerBodyProps {
  hasLoadingRequests: boolean;
  onCancelClickButton: () => void;
  onDeleteClickButton: (checklist: Checklist) => void;
  checklist?: Checklist;
}

export interface FormProps {
  checklistName: string;
  checklistItems: TemplateChecklistField[];
}

export const DrawerBody: React.FC<DrawerBodyProps> = ({
  hasLoadingRequests,
  onCancelClickButton,
  checklist,
  onDeleteClickButton,
}) => {
  const { values, resetForm } = useFormikContext<FormProps>();
  const handleOnClick =
    (form: FieldArrayRenderProps) =>
    (option: DropdownOption<ChecklistFieldType>) => {
      Analytics.track('New Checklist Field Added', {
        checklistFieldType: option.key,
      });

      form.push({
        type: ChecklistFieldType[option.key],
        title: '',
        required: false,
        content: checklistDefaultContent[option.key],
      });
    };
  const handleOnDeleteItem =
    (form: FieldArrayRenderProps, index: number) => () => {
      Analytics.track('Checklist Field Removed');

      form.remove(index);
    };

  const handleCloseDrawer = () => {
    const eventType = checklist ? 'Update' : 'Create';
    Analytics.track(`${eventType} Checklist Cancelled`);

    onCancelClickButton();
    resetForm();
  };

  return (
    <FieldArray
      name='checklistItems'
      render={(form) => (
        <div className={styles.body}>
          <div>
            {!checklist && (
              <div className={styles.bodyTip}>
                <InfoIcon className={styles.bodyTipIcon} />
                <CaptionDefault>
                  Dica: Essa checklist deverá ser atrelada à algum cliente?
                  Adicione um item do tipo ”Campo de texto” com descrição
                  ”Cliente” e marque-o como obrigatório.
                </CaptionDefault>
              </div>
            )}
            <div className={checklist ? '' : styles.formWrapper}>
              {values.checklistItems?.map(
                (checklistItem: Partial<ChecklistField>, index: number) => (
                  <div key={checklistItem.id || index} className={styles.form}>
                    <ChecklistItem
                      handleDeleteClick={handleOnDeleteItem(form, index)}
                      index={index}
                    />
                  </div>
                )
              )}
            </div>
            <div className={styles.textButton}>
              <ChecklistItemDropdownMenu onClick={handleOnClick(form)} />
            </div>
          </div>
          <FooterButtons
            onCancelClickButton={handleCloseDrawer}
            onDeleteClickButton={onDeleteClickButton}
            hasLoadingRequests={hasLoadingRequests}
            checklist={checklist}
          />
        </div>
      )}
    />
  );
};

export default DrawerBody;
